import { useState, useEffect, useCallback } from 'react';
import { db } from '../data/storage';
import { cacheManager } from '../data/cacheManager';
import type { Animal, AnimalWithStats, Feeding, Weight } from '../data/types';

export function useAnimals() {
    const [animals, setAnimals] = useState<AnimalWithStats[]>([]);
    const [loading, setLoading] = useState(true);
    const [pendingSyncs, setPendingSyncs] = useState<string[]>([]);
    const [syncing, setSyncing] = useState(false);

    // Compute stats for animals
    const computeStats = useCallback((
        animalsData: Animal[],
        feedingsData: Feeding[],
        weightsData: Weight[]
    ): AnimalWithStats[] => {
        return animalsData
            .filter(a => a.status === 'active')
            .map(animal => {
                const animalFeedings = feedingsData.filter(f => f.animalId === animal.id);
                const animalWeights = weightsData.filter(w => w.animalId === animal.id);

                const lastFeeding = animalFeedings[0];
                const lastWeight = animalWeights[0];

                let nextDueTime: number | undefined;
                let isDue = false;
                let isOverdue = false;

                if (lastFeeding) {
                    nextDueTime = lastFeeding.timestamp + (animal.feedingIntervalHours * 60 * 60 * 1000);
                    const now = Date.now();
                    const timeUntilDue = nextDueTime - now;
                    isDue = timeUntilDue <= 15 * 60 * 1000 && timeUntilDue > 0;
                    isOverdue = timeUntilDue <= 0;
                }

                return {
                    ...animal,
                    lastFeeding,
                    nextDueTime,
                    lastWeight,
                    isDue,
                    isOverdue,
                };
            })
            .sort((a, b) => {
                if (a.isOverdue && !b.isOverdue) return -1;
                if (!a.isOverdue && b.isOverdue) return 1;
                if (a.isDue && !b.isDue) return -1;
                if (!a.isDue && b.isDue) return 1;
                if (a.nextDueTime && b.nextDueTime) {
                    return a.nextDueTime - b.nextDueTime;
                }
                return 0;
            });
    }, []);

    // Load from cache immediately, then sync with IndexedDB
    const loadAnimals = useCallback(async () => {
        try {
            // 1. Try to load from cache first (instant)
            const cached = cacheManager.loadCache();
            if (cached) {
                const cachedStats = computeStats(cached.animals, cached.feedings, cached.weights);
                setAnimals(cachedStats);
                setLoading(false); // UI is ready!
            }

            // 2. Load from IndexedDB (authoritative source)
            const [animalsData, feedingsData, weightsData] = await Promise.all([
                db.getAnimals(),
                db.getFeedings(),
                db.getWeights(),
            ]);

            // 3. Update cache
            cacheManager.saveCache({
                animals: animalsData,
                feedings: feedingsData,
                weights: weightsData,
                lastSync: Date.now(),
            });

            // 4. Update UI with fresh data
            const stats = computeStats(animalsData, feedingsData, weightsData);
            setAnimals(stats);
            setLoading(false);
        } catch (error) {
            console.error('Error loading animals:', error);
            setLoading(false);
        }
    }, [computeStats]);

    // Retry pending syncs
    const retryPendingSyncs = useCallback(async () => {
        const pending = cacheManager.getPendingSyncs();
        if (pending.length === 0) return;

        setSyncing(true);
        console.log(`Retrying ${pending.length} pending syncs...`);

        for (const sync of pending) {
            try {
                // Perform the sync operation
                switch (sync.type) {
                    case 'animal':
                        if (sync.operation === 'add') {
                            await db.addAnimal(sync.data);
                        } else if (sync.operation === 'update') {
                            await db.updateAnimal(sync.data);
                        } else if (sync.operation === 'delete') {
                            await db.deleteAnimal(sync.data);
                        }
                        break;
                    case 'feeding':
                        if (sync.operation === 'add') {
                            await db.addFeeding(sync.data);
                        }
                        break;
                    case 'weight':
                        if (sync.operation === 'add') {
                            await db.addWeight(sync.data);
                        }
                        break;
                }

                // Remove from pending queue
                cacheManager.removePendingSync(sync.id);
                setPendingSyncs(prev => prev.filter(id => id !== sync.id));
            } catch (error) {
                console.error('Failed to sync:', sync, error);
                // Keep in queue to retry later
            }
        }

        setSyncing(false);
        await loadAnimals(); // Refresh after syncing
    }, [loadAnimals]);

    // Initial load
    useEffect(() => {
        const init = async () => {
            // Load data (cache first, then DB)
            await loadAnimals();

            // Retry any pending syncs
            await retryPendingSyncs();
        };

        init();

        // Refresh every 30 seconds to update due/overdue status
        const interval = setInterval(() => {
            // Only recompute stats, don't reload from DB unless cache is stale
            if (cacheManager.isCacheStale()) {
                loadAnimals();
            } else {
                const cached = cacheManager.loadCache();
                if (cached) {
                    const stats = computeStats(cached.animals, cached.feedings, cached.weights);
                    setAnimals(stats);
                }
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [loadAnimals, retryPendingSyncs, computeStats]);

    // Optimistic add animal
    const addAnimal = async (animal: Animal) => {
        const syncId = `animal-add-${animal.id}`;

        // 1. Update cache immediately (optimistic)
        cacheManager.updateCacheOptimistically('animal', 'add', animal);

        // 2. Update UI immediately
        const cached = cacheManager.loadCache();
        if (cached) {
            const stats = computeStats(cached.animals, cached.feedings, cached.weights);
            setAnimals(stats);
        }

        // 3. Mark as pending sync
        cacheManager.addPendingSync({
            id: syncId,
            type: 'animal',
            operation: 'add',
            data: animal,
        });
        setPendingSyncs(prev => [...prev, syncId]);

        // 4. Sync to IndexedDB (background)
        try {
            await db.addAnimal(animal);
            cacheManager.removePendingSync(syncId);
            setPendingSyncs(prev => prev.filter(id => id !== syncId));
        } catch (error) {
            console.error('Failed to add animal:', error);
            // Will retry on next app load
        }
    };

    // Optimistic update animal
    const updateAnimal = async (animal: Animal) => {
        const syncId = `animal-update-${animal.id}`;

        // 1. Update cache immediately
        cacheManager.updateCacheOptimistically('animal', 'update', animal);

        // 2. Update UI immediately
        const cached = cacheManager.loadCache();
        if (cached) {
            const stats = computeStats(cached.animals, cached.feedings, cached.weights);
            setAnimals(stats);
        }

        // 3. Mark as pending
        cacheManager.addPendingSync({
            id: syncId,
            type: 'animal',
            operation: 'update',
            data: animal,
        });
        setPendingSyncs(prev => [...prev, syncId]);

        // 4. Sync to IndexedDB
        try {
            await db.updateAnimal(animal);
            cacheManager.removePendingSync(syncId);
            setPendingSyncs(prev => prev.filter(id => id !== syncId));
        } catch (error) {
            console.error('Failed to update animal:', error);
        }
    };

    // Optimistic delete animal
    const deleteAnimal = async (id: string) => {
        const syncId = `animal-delete-${id}`;

        // 1. Update cache immediately
        cacheManager.updateCacheOptimistically('animal', 'delete', id);

        // 2. Update UI immediately
        const cached = cacheManager.loadCache();
        if (cached) {
            const stats = computeStats(cached.animals, cached.feedings, cached.weights);
            setAnimals(stats);
        }

        // 3. Mark as pending
        cacheManager.addPendingSync({
            id: syncId,
            type: 'animal',
            operation: 'delete',
            data: id,
        });
        setPendingSyncs(prev => [...prev, syncId]);

        // 4. Sync to IndexedDB
        try {
            await db.deleteAnimal(id);
            cacheManager.removePendingSync(syncId);
            setPendingSyncs(prev => prev.filter(id => id !== syncId));
        } catch (error) {
            console.error('Failed to delete animal:', error);
        }
    };

    const refresh = loadAnimals;

    return {
        animals,
        loading,
        pendingSyncs, // Expose pending syncs for UI indicators
        syncing, // Expose syncing state
        addAnimal,
        updateAnimal,
        deleteAnimal,
        refresh,
    };
}